import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { COMPANY, waLink } from "@/lib/constants";

export default function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();

  const nav = [
    { id: "services", label: t("nav.services") },
    { id: "gallery", label: t("nav.gallery") },
    { id: "about", label: t("nav.about") },
    { id: "contact", label: t("nav.contact") },
  ];

  const services = [
    t("services.items.renovation.name"),
    t("services.items.bathroom.name"),
    t("services.items.solar_ac.name"),
    t("services.items.painting.name"),
  ];

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#050505] text-white pt-20 md:pt-28 pb-10 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src={COMPANY.logo_url}
                alt="Modolo Multiservices"
                className="h-14 w-auto object-contain"
              />
              <div className="leading-tight">
                <div className="font-display font-extrabold text-xl tracking-tight">Modolo</div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-white/60">Multiservices</div>
              </div>
            </div>
            <p className="mt-6 text-white/60 text-sm max-w-sm leading-relaxed">
              {t("footer.tagline")}
            </p>
            <div className="mt-6 text-xs text-white/45">
              {t("footer.cif")} {COMPANY.cif}
            </div>
          </div>

          {/* Nav */}
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 font-bold mb-4">
              {t("footer.links_title")}
            </div>
            <ul className="space-y-3">
              {nav.map((l) => (
                <li key={l.id}>
                  <button
                    data-testid={`footer-nav-${l.id}`}
                    onClick={() => go(l.id)}
                    className="text-sm text-white/80 hover:text-[#1F9D40] transition-colors"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 font-bold mb-4">
              {t("footer.services_title")}
            </div>
            <ul className="space-y-3">
              {services.map((s) => (
                <li key={s} className="text-sm text-white/80">
                  {s}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 font-bold mb-4">
              {t("footer.contact_title")}
            </div>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${COMPANY.phone_raw}`}
                  data-testid="footer-phone"
                  className="flex items-start gap-3 text-sm text-white/85 hover:text-[#1F9D40] transition-colors"
                >
                  <Phone size={14} className="mt-1 text-[#1F9D40]" />
                  {COMPANY.phone_display}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="footer-email"
                  className="flex items-start gap-3 text-sm text-white/85 hover:text-[#1F9D40] transition-colors break-all"
                >
                  <Mail size={14} className="mt-1 text-[#1F9D40] shrink-0" />
                  {COMPANY.email}
                </a>
              </li>
              <li>
                <a
                  href={COMPANY.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-map"
                  className="flex items-start gap-3 text-sm text-white/85 hover:text-[#1F9D40] transition-colors"
                >
                  <MapPin size={14} className="mt-1 text-[#1F9D40] shrink-0" />
                  <span>
                    {COMPANY.address_line1}
                    <br />
                    {COMPANY.address_line2}
                    <br />
                    <span className="text-[#1F9D40] text-xs underline">{t("footer.map")}</span>
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={waLink("Hola Modolo, quiero pedir un presupuesto.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp"
                  className="flex items-start gap-3 text-sm text-[#25D366] hover:text-white transition-colors"
                >
                  <MessageCircle size={14} className="mt-1" />
                  WhatsApp
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-xs text-white/45">
            © {year} Modolo Multiservices. {t("footer.rights")}
          </div>
          <div className="text-xs text-white/45">{t("footer.made")}</div>
        </div>
      </div>
    </footer>
  );
}
